import { useCallback, useContext, useEffect, useState } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	FASTSHIFT_ASSESSMENT_BLOCKER_STATUSES,
	FastshiftExpectedError,
	type FastshiftAssessmentResult,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { PagePanelContext } from '@/common/contexts/page-panel-context/page-panel-context';
import { FourOhFourDisplay } from '@/common/displays';
import { GenericErrorDisplay } from '@/common/displays/src/generic-error-display/generic-error-display';
import { FastshiftPageBreadcrumbs } from '@/common/fastshift-page-breadcrumbs/fastshift-page-breadcrumbs';
import {
	FASTSHIFT_PRODUCT_NAMES,
	FASTSHIFT_PRODUCT_VARIANTS,
	FastshiftProductIcon,
} from '@/common/fastshift-product-icon/src/fastshift-product-icon';
import { useRoutes } from '@/common/hooks';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import { getFastshiftDcInstanceDetails } from '@/state/actions';
import type { DcInstanceDetail } from '@/state/types';

import {
	type RecommendationDetailsEntryPoint,
	useDcInstanceDetailsPageAnalytics,
} from './hooks/use-dc-instance-details-page-analytics';
import { useOrganizationBreadcrumb } from './hooks/use-organization-breadcrumb';
import { messages } from './messages';
import { AssessmentOverviewSection } from './src/assessment-overview-section/assessment-overview-section';
import { AssessmentRecommendationDetailsPanel } from './src/assessment-recommendation-details-panel/assessment-recommendation-details-panel';
import { AssessmentResultsSection } from './src/assessment-results-section/assessment-results-section';
import { InstanceSummarySection } from './src/instance-summary-section/instance-summary-section';
import { TopRecommendationSection } from './src/top-recommendation-section/top-recommendation-section';

type DcInstanceRoute = {
	domainId: string;
	organizationId: string;
	instanceId: string;
};

type RouteScopedState = {
	domainId: string | null;
	orgId: string | null;
	instanceId: string | null;
};

const isRouteScoped = (
	details: RouteScopedState,
	{ domainId, organizationId, instanceId }: DcInstanceRoute,
) =>
	details.domainId === domainId &&
	details.orgId === organizationId &&
	details.instanceId === instanceId;

const getRouteScopedDetails = <TDetails extends DcInstanceDetail>({
	details,
	domainId,
	organizationId,
	instanceId,
}: {
	details: TDetails;
} & DcInstanceRoute): Pick<TDetails, 'pending' | 'error' | 'data'> => {
	if (isRouteScoped(details, { domainId, organizationId, instanceId })) {
		return {
			pending: details.pending,
			error: details.error,
			data: details.data,
		};
	}

	return {
		pending: true,
		error: null,
		data: null,
	};
};

const getNextSelectedResult = (
	currentResult: FastshiftAssessmentResult | null,
	selectedResult: FastshiftAssessmentResult,
) => (currentResult?.id === selectedResult.id ? null : selectedResult);

const getSelectedResultId = (selectedResult: FastshiftAssessmentResult | null) =>
	selectedResult?.id ?? null;

const hasDcInstanceLoadError = ({
	pending,
	error,
	data,
}: Pick<DcInstanceDetail, 'pending' | 'error' | 'data'>) => !pending && Boolean(error || !data);

const getDcInstanceProductLabelMessage = (productType: string | null) => {
	switch (productType) {
		case FASTSHIFT_PRODUCT_NAMES.JIRA:
			return messages.jiraDataCenterLabel;
		case FASTSHIFT_PRODUCT_NAMES.CONFLUENCE:
			return messages.confluenceDataCenterLabel;
		default:
			return messages.dataCenterLabelFallback;
	}
};

const getInstanceSummaryFields = (data: DcInstanceDetail['data']) => ({
	productType: data?.productType ?? null,
	version: data?.version,
	connectionStatus: data?.connectionStatus,
	serverId: data?.serverId,
});

const styles = cssMap({
	productIconTile: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.neutral'),
	},
});

const FastshiftDcInstanceDetailsPageContent = ({
	domainId,
	organizationId,
	instanceId,
}: DcInstanceRoute) => {
	const { formatMessage } = useIntl();
	const { paths } = useRoutes();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const [selectedResult, setSelectedResult] = useState<FastshiftAssessmentResult | null>(null);
	const { openPanel, closePanel } = useContext(PagePanelContext);
	const dcInstanceDetail = useFastshiftSelector((state) => state.dcInstanceDetail);
	const { isLoading: isOrganizationNameLoading, organizationName } = useOrganizationBreadcrumb({
		domainId,
		organizationId,
	});

	const loadDetails = useCallback(() => {
		dispatch(
			getFastshiftDcInstanceDetails({
				stargateClient,
				domainId,
				orgId: organizationId,
				instanceId,
			}),
		);
	}, [dispatch, domainId, instanceId, organizationId, stargateClient]);

	useEffect(() => {
		loadDetails();
	}, [loadDetails]);

	const { pending, error, data } = getRouteScopedDetails({
		details: dcInstanceDetail,
		domainId,
		organizationId,
		instanceId,
	});
	const { sendAssessmentFilterChangeUIEvent, sendRecommendationDetailsClickUIEvent } =
		useDcInstanceDetailsPageAnalytics({
			organizationId,
			details: { pending, error, data },
		});
	const assessment = data?.assessment;
	const openRecommendationDetails = useCallback(
		(recommendationId: string, entryPoint: RecommendationDetailsEntryPoint) => {
			if (!assessment) {
				return;
			}
			const assessmentResult = assessment.results.find(({ id }) => id === recommendationId);
			sendRecommendationDetailsClickUIEvent(recommendationId, entryPoint);

			const panelTrigger =
				document.activeElement instanceof HTMLElement ? document.activeElement : null;

			const closeRecommendationDetails = () => {
				closePanel();
				if (panelTrigger?.isConnected) {
					panelTrigger.focus();
				}
			};

			openPanel(
				<AssessmentRecommendationDetailsPanel
					domainId={domainId}
					organizationId={organizationId}
					instanceId={instanceId}
					assessmentId={assessment.assessmentId}
					recommendationId={recommendationId}
					dataPoint={
						assessmentResult?.dataPoint ?? assessment.topRecommendation?.title ?? recommendationId
					}
					status={assessmentResult?.impact?.status ?? null}
					blockerStatus={
						assessmentResult?.blockerStatus ?? FASTSHIFT_ASSESSMENT_BLOCKER_STATUSES.NON_BLOCKER
					}
					onClose={closeRecommendationDetails}
				/>,
			);
		},
		[
			assessment,
			closePanel,
			domainId,
			instanceId,
			openPanel,
			organizationId,
			sendRecommendationDetailsClickUIEvent,
		],
	);
	const handleResultSelect = useCallback(
		(result: FastshiftAssessmentResult) => {
			setSelectedResult((currentResult) => getNextSelectedResult(currentResult, result));
			openRecommendationDetails(result.id, 'assessmentResult');
		},
		[openRecommendationDetails],
	);
	const handleTopRecommendationViewDetails = useCallback(
		(recommendationId: string) => {
			setSelectedResult(assessment?.results.find(({ id }) => id === recommendationId) ?? null);
			openRecommendationDetails(recommendationId, 'topRecommendation');
		},
		[assessment, openRecommendationDetails],
	);

	if (!pending && error === FastshiftExpectedError.NOT_FOUND) {
		return <FourOhFourDisplay />;
	}

	const hasLoadError = hasDcInstanceLoadError({ pending, error, data });
	const pageTitle = data?.baseUrl ?? formatMessage(messages.pageTitleFallback);
	const {
		productType: instanceSummaryProductType,
		version,
		connectionStatus,
		serverId,
	} = getInstanceSummaryFields(data);
	const productType = instanceSummaryProductType ?? null;
	const { assessedAt, assessmentStatus } = assessment ?? {};
	const topRecommendation = assessment?.topRecommendation ?? null;
	const assessmentResults = assessment?.results ?? [];

	return (
		<Stack space="space.300">
			<FastshiftPageBreadcrumbs
				isLoading={pending || isOrganizationNameLoading}
				items={[
					{
						href: paths.domainScopedOrganizations,
						text: formatMessage(messages.organizationsBreadcrumb),
					},
					{
						href: paths.domainScopedOrganizationDetails,
						text: organizationName,
					},
					{
						href: paths.domainScopedDcInstanceDetails,
						text: pageTitle,
					},
				]}
				loadingLabel={formatMessage(messages.loadingLabel)}
				skeletonWidths={[100, 120, 160]}
				testId="dc-instance-details-breadcrumb-skeleton"
			/>
			{pending ? (
				<Skeleton
					height="32px"
					width="300px"
					isShimmering
					testId="dc-instance-details-heading-skeleton"
				/>
			) : (
				<Inline space="space.200" alignBlock="center">
					<Inline
						alignBlock="center"
						alignInline="center"
						xcss={styles['productIconTile']}
						testId="dc-instance-product-icon-tile"
					>
						<FastshiftProductIcon
							productName={productType}
							variant={FASTSHIFT_PRODUCT_VARIANTS.SOURCE}
						/>
					</Inline>
					<Stack space="space.050">
						<Text size="large">{formatMessage(getDcInstanceProductLabelMessage(productType))}</Text>
						<Heading size="medium" as="h1">
							{pageTitle}
						</Heading>
					</Stack>
				</Inline>
			)}
			{hasLoadError ? (
				<GenericErrorDisplay
					headingAs="h2"
					customMessage={formatMessage(messages.errorDescription)}
				/>
			) : (
				<>
					<InstanceSummarySection
						isLoading={pending}
						productType={instanceSummaryProductType}
						version={version}
						connectionStatus={connectionStatus}
						serverId={serverId}
					/>
					<AssessmentOverviewSection
						isLoading={pending}
						results={assessmentResults}
						assessedAt={assessedAt}
						assessmentStatus={assessmentStatus}
					/>
					<TopRecommendationSection
						isLoading={pending}
						topRecommendation={topRecommendation}
						results={assessmentResults}
						onViewDetails={handleTopRecommendationViewDetails}
					/>
					<AssessmentResultsSection
						isLoading={pending}
						results={assessmentResults}
						selectedResultId={getSelectedResultId(selectedResult)}
						onResultSelect={handleResultSelect}
						onFilterChange={sendAssessmentFilterChangeUIEvent}
					/>
				</>
			)}
		</Stack>
	);
};

export const FastshiftDcInstanceDetailsPage = () => {
	const { domainId, organizationId, instanceId } = useParams<{
		domainId: string;
		organizationId: string;
		instanceId: string;
	}>();

	if (!domainId || !organizationId || !instanceId) {
		return <FourOhFourDisplay />;
	}

	const routeKey = `${domainId}:${organizationId}:${instanceId}`;

	return (
		<FastshiftDcInstanceDetailsPageContent
			key={routeKey}
			domainId={domainId}
			organizationId={organizationId}
			instanceId={instanceId}
		/>
	);
};
